/**
 * Parses stored coveragesAndLimits XML from an ACORD 27 master certificate
 * back into Acord27Form prefill state.
 *
 * XML tag → form state mapping (derived from buildAcord27Xml + NowCerts stored format):
 *   ProducerFullName         → agencyAddress1
 *   ProducerAddressLineOne   → agencyAddress2
 *   ProducerAddressLineTwo   → agencyAddress3
 *   ProducerCityName         → agencyCity
 *   ProducerStateOrProvinceCode → agencyState
 *   ProducerPostalCode       → agencyZip
 *   Phone / Fax / Email      → agencyPhone / agencyFax / agencyEmail
 *   AgencyCode/SubCode/Customer → agencyCode / agencySubCode / agencyCustomer
 *   Company                  → company
 *   LoanNumber               → loanNumber
 *   ContinuedUntilTerminated → continuedUntilTerminated
 *   PriorEvidanceDate / ReplacesPriorDate → replacesPriorDate  (NowCerts typo handled)
 *   ProducerCompletionDate   → certDate
 *   NamedInsuredFullName     → insuredAddress1
 *   NamedInsuredLineOne      → insuredAddress2
 *   NamedInsuredLineTwo      → insuredAddress3
 *   NamedInsuredCityName     → insuredCity
 *   NamedInsuredState        → insuredState
 *   NamedInsuredZIP          → insuredZip
 *   LocationDescription      → locationDescription
 *   Other1..Other10          → coverageRows[0..9]
 *     text attr  → coveragePerilsForms
 *     textContent → amountOfInsurance   (NowCerts swaps value↔text from our builder)
 *     value attr → deductible
 *   Remarks                  → remarks
 *   AdditionalInterestName   → additionalInterestName
 *   Mortgagee                → aiMortgagee
 *   AdditionalInsured        → aiAdditionalInsured
 *   LossPayee                → aiLossPayee
 *   OtherInterest            → aiOtherCheck (text attr) / aiOtherText (content)
 *   AdditionalInterestLoanNumber → aiLoanNumber
 *
 * policyNumber / effectiveDate / expirationDate come from coverage entry metadata.
 * company falls back to policies[n].carrierName when not in XML.
 */

import type { StoredCoverage, StoredPolicy } from './parseAcord25StoredCoverages';
export type { StoredCoverage, StoredPolicy };

export interface Acord27EditPrefill {
  certDate: Date | null;
  agencyAddress1: string;
  agencyAddress2: string;
  agencyAddress3: string;
  agencyCity: string;
  agencyState: string;
  agencyZip: string;
  agencyPhone: string;
  agencyFax: string;
  agencyEmail: string;
  agencyCode: string;
  agencySubCode: string;
  agencyCustomer: string;
  company: string;
  loanNumber: string;
  policyNumber: string;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  continuedUntilTerminated: boolean;
  replacesPriorDate: Date | null;
  insuredAddress1: string;
  insuredAddress2: string;
  insuredAddress3: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
  locationDescription: string;
  coverageRows: { coveragePerilsForms: string; amountOfInsurance: string; deductible: string }[];
  // v2016 perils fields
  perilsInsured: boolean;
  basic: boolean;
  broad: boolean;
  special: boolean;
  otherCoverageCheck: boolean;
  otherCoverageText: string;
  remarks: string;
  additionalInterestName: string;
  aiMortgagee: boolean;
  aiAdditionalInsured: boolean;
  aiLossPayee: boolean;
  aiLenderLossPayable: boolean;
  aiOtherCheck: boolean;
  aiOtherText: string;
  aiLoanNumber: string;
  authRepSignature: string;
  acord101AdditionalRemarks: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseDoc(xml: string): Document {
  return new DOMParser().parseFromString(`<root>${xml}</root>`, 'text/xml');
}

function getText(doc: Document, selector: string): string {
  return doc.querySelector(selector)?.textContent?.trim() || '';
}

function yn(v: string): boolean {
  return ['yes', 'true', '1'].includes(v.trim().toLowerCase());
}

function parseStoredDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  const slash = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) return new Date(Number(slash[3]), Number(slash[1]) - 1, Number(slash[2]));
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

const COVERAGE_ROW_COUNT = 10;

// ── Main export ───────────────────────────────────────────────────────────────

export function parseAcord27StoredCoverages(
  coverages: StoredCoverage[],
  policies: StoredPolicy[],
): Acord27EditPrefill {
  const emptyRows = Array.from({ length: COVERAGE_ROW_COUNT }, () => ({
    coveragePerilsForms: '',
    amountOfInsurance: '',
    deductible: '',
  }));

  const result: Acord27EditPrefill = {
    certDate: null,
    agencyAddress1: '', agencyAddress2: '', agencyAddress3: '',
    agencyCity: '', agencyState: '', agencyZip: '',
    agencyPhone: '', agencyFax: '', agencyEmail: '',
    agencyCode: '', agencySubCode: '', agencyCustomer: '',
    company: '',
    loanNumber: '', policyNumber: '',
    effectiveDate: null, expirationDate: null,
    continuedUntilTerminated: false, replacesPriorDate: null,
    insuredAddress1: '', insuredAddress2: '', insuredAddress3: '',
    insuredCity: '', insuredState: '', insuredZip: '',
    locationDescription: '',
    coverageRows: emptyRows,
    perilsInsured: false, basic: false, broad: false, special: false,
    otherCoverageCheck: false, otherCoverageText: '',
    remarks: '',
    additionalInterestName: '',
    aiMortgagee: false, aiAdditionalInsured: false, aiLossPayee: false, aiLenderLossPayable: false,
    aiOtherCheck: false, aiOtherText: '', aiLoanNumber: '',
    authRepSignature: '',
    acord101AdditionalRemarks: '',
  };

  // Build policyNumber → carrierName lookup
  const policyCarrierMap: Record<string, string> = {};
  for (const p of policies) {
    if (p.number && p.carrierName) policyCarrierMap[p.number] = p.carrierName;
  }

  for (const cov of coverages) {
    if (cov.signatureId && !result.authRepSignature) result.authRepSignature = cov.signatureId;
    if (!cov.coveragesAndLimits) continue;

    if (cov.acordFormNumber === 101) {
      const doc = parseDoc(cov.coveragesAndLimits);
      result.acord101AdditionalRemarks = getText(doc, 'Acord101AdditionalRemarks RemarksText');
      continue;
    }

    // 27101 = acord27_101 composite form number stored by the server
    if (cov.acordFormNumber !== 27 && cov.acordFormNumber !== 2716 && cov.acordFormNumber !== 27101) {
      // Fallback: catch any entry whose form number was normalized away from 101
      const doc = parseDoc(cov.coveragesAndLimits);
      const remarksText = getText(doc, 'Acord101AdditionalRemarks RemarksText');
      if (remarksText) result.acord101AdditionalRemarks = remarksText;
      continue;
    }

    const doc = parseDoc(cov.coveragesAndLimits);
    if (!doc.querySelector('Acord27AllLiabilities')) {
      // Entry passed form-number check but XML is not a main ACORD 27 block (e.g. a 101 entry
      // whose acordFormNumber was normalized to 27/2716/27101 by the server)
      const remarksText = getText(doc, 'Acord101AdditionalRemarks RemarksText');
      if (remarksText) result.acord101AdditionalRemarks = remarksText;
      continue;
    }

    // Coverage entry metadata
    result.policyNumber  = cov.policyNumber || '';
    result.effectiveDate  = parseStoredDate(cov.effectiveDate);
    result.expirationDate = parseStoredDate(cov.expirationDate);

    // Agency / producer
    result.certDate       = parseStoredDate(getText(doc, 'Acord27AllLiabilities ProducerCompletionDate'));
    result.agencyAddress1 = getText(doc, 'Acord27AllLiabilities ProducerFullName');
    result.agencyAddress2 = getText(doc, 'Acord27AllLiabilities ProducerAddressLineOne');
    result.agencyAddress3 = getText(doc, 'Acord27AllLiabilities ProducerAddressLineTwo');
    result.agencyCity     = getText(doc, 'Acord27AllLiabilities ProducerCityName');
    result.agencyState    = getText(doc, 'Acord27AllLiabilities ProducerStateOrProvinceCode');
    result.agencyZip      = getText(doc, 'Acord27AllLiabilities ProducerPostalCode');
    result.agencyPhone    = getText(doc, 'Acord27AllLiabilities Phone');
    result.agencyFax      = getText(doc, 'Acord27AllLiabilities Fax');
    result.agencyEmail    = getText(doc, 'Acord27AllLiabilities Email');
    result.agencyCode     = getText(doc, 'Acord27AllLiabilities AgencyCode');
    result.agencySubCode  = getText(doc, 'Acord27AllLiabilities AgencySubCode');
    result.agencyCustomer = getText(doc, 'Acord27AllLiabilities AgencyCustomer');

    // Company — from XML first, then policy carrier fallback
    result.company = getText(doc, 'Acord27AllLiabilities Company')
      || policyCarrierMap[cov.policyNumber] || '';

    // Loan / policy
    result.loanNumber = getText(doc, 'Acord27AllLiabilities LoanNumber');
    result.continuedUntilTerminated = yn(getText(doc, 'Acord27AllLiabilities ContinuedUntilTerminated'));

    // NowCerts stores "PriorEvidanceDate" (typo); our builder writes "ReplacesPriorDate"
    const priorDateStr =
      getText(doc, 'Acord27AllLiabilities PriorEvidanceDate') ||
      getText(doc, 'Acord27AllLiabilities ReplacesPriorDate');
    result.replacesPriorDate = parseStoredDate(priorDateStr);

    // Insured
    result.insuredAddress1 = getText(doc, 'Acord27AllLiabilities NamedInsuredFullName');
    result.insuredAddress2 = getText(doc, 'Acord27AllLiabilities NamedInsuredLineOne');
    result.insuredAddress3 = getText(doc, 'Acord27AllLiabilities NamedInsuredLineTwo');
    result.insuredCity     = getText(doc, 'Acord27AllLiabilities NamedInsuredCityName');
    result.insuredState    = getText(doc, 'Acord27AllLiabilities NamedInsuredState');
    result.insuredZip      = getText(doc, 'Acord27AllLiabilities NamedInsuredZIP');

    result.locationDescription = getText(doc, 'Acord27AllLiabilities LocationDescription');

    // Coverage rows Other1..Other10
    // NowCerts stores: text attr = coveragePerilsForms, textContent = amountOfInsurance, value attr = deductible
    const rows = Array.from({ length: COVERAGE_ROW_COUNT }, (_, i) => {
      const el = doc.querySelector(`Acord27AllLiabilities Other${i + 1}`);
      if (!el) return { coveragePerilsForms: '', amountOfInsurance: '', deductible: '' };
      return {
        coveragePerilsForms: el.getAttribute('text') || '',
        amountOfInsurance:   el.textContent?.trim() || '',
        deductible:          el.getAttribute('value') || '',
      };
    });
    result.coverageRows = rows;

    result.remarks = getText(doc, 'Acord27AllLiabilities Remarks');

    // v2016 perils fields
    result.perilsInsured      = yn(getText(doc, 'Acord27AllLiabilities PerilsInsured'));
    result.basic              = yn(getText(doc, 'Acord27AllLiabilities Basic'));
    result.broad              = yn(getText(doc, 'Acord27AllLiabilities Broad'));
    result.special            = yn(getText(doc, 'Acord27AllLiabilities Special'));
    const otherCovEl = doc.querySelector('Acord27AllLiabilities OtherCoverage');
    result.otherCoverageCheck = yn(otherCovEl?.textContent?.trim() || '');
    result.otherCoverageText  = otherCovEl?.getAttribute('text') || '';

    // Additional interest
    result.additionalInterestName = getText(doc, 'Acord27AllLiabilities AdditionalInterestName');
    result.aiMortgagee            = yn(getText(doc, 'Acord27AllLiabilities Mortgagee'));
    result.aiAdditionalInsured    = yn(getText(doc, 'Acord27AllLiabilities AdditionalInsured'));
    result.aiLossPayee            = yn(getText(doc, 'Acord27AllLiabilities LossPayee'));
    result.aiLenderLossPayable    = yn(getText(doc, 'Acord27AllLiabilities LenderLossPayable'));
    const otherInterestEl = doc.querySelector('Acord27AllLiabilities OtherInterest');
    result.aiOtherCheck = yn(otherInterestEl?.getAttribute('text') || '');
    result.aiOtherText  = otherInterestEl?.textContent?.trim() || '';
    result.aiLoanNumber = getText(doc, 'Acord27AllLiabilities AdditionalInterestLoanNumber');

    // continue processing — may still have an ACORD 101 entry
  }

  return result;
}
